// ==========================================================================
// BMI CALCULATOR - CALCULATION LOGIC
// ==========================================================================

export type BmiGender = 'man' | 'vrouw'
export type BmiCategory = 0 | 1 | 2 | 3

// Calculates the BMI value based on length (cm) and weight (kg).
export const calculateBmi = (length: number, weight: number): number => {
  return weight / (length / 100) ** 2
}

// The cut off values define whether a certain BMI value for a specific age is
// underweight, ok, overweight or obese. getBmiCategory() below uses these to
// determine the category of a given BMI value.
const cutOffValues: Record<BmiGender, Record<number, [number, number, number]>> = {
  vrouw: {
    2: [13.85, 18.05, 19.75],
    3: [13.55, 17.55, 19.35],
    4: [13.25, 17.35, 19.15],
    5: [13.05, 17.15, 19.15],
    6: [12.85, 17.25, 19.55],
    7: [12.85, 17.65, 20.35],
    8: [12.95, 18.25, 21.35],
    9: [13.15, 18.95, 22.65],
    10: [13.35, 19.75, 23.95],
    11: [13.75, 20.65, 25.25],
    12: [14.25, 21.55, 26.45],
    13: [14.85, 22.45, 27.55],
    14: [15.35, 23.25, 28.35],
    15: [15.95, 23.85, 28.95],
    16: [16.35, 24.25, 29.45],
    17: [16.75, 24.65, 29.65],
    18: [18.45, 24.95, 29.95],
  },
  man: {
    2: [14.05, 18.35, 19.95],
    3: [13.75, 17.85, 19.45],
    4: [13.45, 17.45, 19.15],
    5: [13.25, 17.35, 19.25],
    6: [13.15, 17.45, 19.75],
    7: [13.05, 17.85, 20.55],
    8: [13.05, 18.35, 21.55],
    9: [13.15, 19.05, 22.65],
    10: [13.45, 19.75, 23.95],
    11: [13.65, 20.45, 25.05],
    12: [14.05, 21.15, 25.95],
    13: [14.45, 21.85, 26.85],
    14: [14.95, 22.55, 27.55],
    15: [15.75, 23.25, 28.25],
    16: [16.05, 23.85, 28.85],
    17: [16.55, 24.45, 29.35],
    18: [18.45, 24.95, 29.95],
  },
}

const toBmiGender = (gender: string): BmiGender => (gender?.toLowerCase() === 'vrouw' ? 'vrouw' : 'man')

// Returns the BMI category (0 = underweight, 1 = ok, 2 = overweight, 3 = obese)
// based on the BMI value, age and gender.
export const getBmiCategory = (bmi: number, age: number, gender: string): BmiCategory => {
  let safeAge = age
  if (safeAge < 2) safeAge = 2
  if (safeAge >= 70) {
    if (bmi < 21.95) return 0
    if (bmi < 27.95) return 1
    if (bmi < 29.95) return 2
    return 3
  }
  if (safeAge > 18) safeAge = 18

  const safeGender = toBmiGender(gender)
  const cutoff = cutOffValues[safeGender][safeAge] || [18.45, 24.95, 29.95]

  if (bmi < cutoff[0]) return 0
  else if (bmi < cutoff[1]) return 1
  else if (bmi < cutoff[2]) return 2
  else return 3
}

// Maps a BMI category to the color modifier class used for the result score.
export const bmiCategoryColor: Record<BmiCategory, 'orange' | 'green' | 'red'> = {
  0: 'orange',
  1: 'green',
  2: 'orange',
  3: 'red',
}

// The waist circumference (middelomtrek) is only relevant for adults from 19 t/m 69 years old.
export const isWaistCircumferenceApplicable = (age: number): boolean => age >= 19 && age <= 69

export type WaistCategory = 0 | 1 | 2

// Cut off values (in cm) for the waist circumference, based on gender.
const waistCutOffValues: Record<BmiGender, [number, number]> = {
  man: [94, 102],
  vrouw: [80, 88],
}

// Returns the waist circumference category (0 = healthy, 1 = increased, 2 = too high)
// based on the waist circumference value (cm) and gender.
export const getWaistCategory = (waist: number, gender: string): WaistCategory => {
  const safeGender = toBmiGender(gender)
  const [increased, tooHigh] = waistCutOffValues[safeGender]

  if (waist < increased) return 0
  else if (waist < tooHigh) return 1
  else return 2
}
